import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from "react-intl";

import Tooltip from "@mui/material/Tooltip";
import {styled} from "@mui/material/styles";

import {ReactComponent as ArrowDouble} from "assets/img/arrow-double.svg";
import {
    black500,
    green50,
    green100,
    green500,
    greyishBlue50,
    greyishBlue600,
    orange50,
    orange100,
    orange500,
    red50,
    red100,
    red500,
    greyishBlue100,
    greyishBlue700,
} from "assets/jss/main";

import Badge from "components/Badge";
import InputTextField from "components/input/new/InputTextField";
import SearchIcon from "components/icons/SearchIcon";
import InfoTooltipIcon from "components/CustomTooltip/InfoTooltipIcon";
import scoringSummary from "api/product/scoringSummary";

import {capitalize} from "utils/capitalize";
import {getScoreLevel} from "views/marketplace/retailer/product/utils/ScoreColorCalculator";

ListingProductPageHeader.propTypes = {
    titleId: PropTypes.string.isRequired,
    switchTooltipId: PropTypes.string,
    onSwitchView: PropTypes.func,
    merchantName: PropTypes.string,
    // bare retailer uuid; the global quality score is fetched once per mount
    // from the scoring summary endpoint, and hidden without one
    retailerId: PropTypes.string,
    searchValue: PropTypes.string,
    searchPlaceholderId: PropTypes.string,
    onSearchChange: PropTypes.func,
    subtitleId: PropTypes.string,
    count: PropTypes.node,
};

const HeaderContainer = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
    width: '100%'
});

const TitlesContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
});

const TitleRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
});

const Title = styled('h2')({
    margin: 'unset',
    fontSize: '24px',
    fontWeight: 700,
    color: black500,
    textTransform: 'none'
});

const SwitchViewButton = styled('div')({
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    cursor: 'pointer',
    flexShrink: 0,
    '&:hover': {
        backgroundColor: greyishBlue50
    },
    'svg': {
        width: '16px',
        height: '16px'
    }
});

const SubtitleRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: greyishBlue600,
    fontSize: '16px',
    fontWeight: 400
});

const QualityScoreContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
});

// indexed by getScoreLevel(): same bands as the product page and the scoring column
const SCORE_COLORS = [
    {background: red50, border: red100, text: red500},
    {background: orange50, border: orange100, text: orange500},
    {background: green50, border: green100, text: green500},
];

const scoreColors = (score) => SCORE_COLORS[getScoreLevel(score)];

const ScorePill = styled('div', {shouldForwardProp: (prop) => 'score' !== prop})(({score}) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    height: '20px',
    padding: '0 8px',
    backgroundColor: scoreColors(score).background,
    border: `1px solid ${scoreColors(score).border}`,
    borderRadius: '48px'
}));

const ScoreValue = styled('span', {shouldForwardProp: (prop) => 'score' !== prop})(({score}) => ({
    color: scoreColors(score).text,
    fontSize: '12px',
    fontWeight: 600
}));

const searchFieldStyle = {
    width: '100%',
    maxWidth: '485px',
    '& .MuiInputBase-root': {
        height: '40px',
        backgroundColor: '#fff',
        paddingLeft: '16px',
        input: {
            padding: '12px 8px 12px 4px',
        },
        'svg': {
            flexShrink: 0
        },
    },
};

function ListingProductPageHeader({titleId, switchTooltipId, onSwitchView, merchantName, retailerId, searchValue, searchPlaceholderId, onSearchChange, subtitleId, count}) {
    const intl = useIntl();
    const [qualityScore, setQualityScore] = useState(null);

    // one call per page visit: the summary aggregates the whole retailer, so
    // filters, sorting and pagination never change it (the header stays
    // mounted through those, only the retailer switch would refetch)
    useEffect(() => {
        if (!retailerId) {
            return;
        }

        scoringSummary(retailerId)
            .then((summary) => setQualityScore(Math.round(summary.averageScore ?? 0)))
            // a failed summary must not break the list: the score just stays hidden
            .catch(() => setQualityScore(null));
    }, [retailerId]);

    return (
        <HeaderContainer>
            <TitlesContainer>
                <TitleRow>
                    <Title>
                        <FormattedMessage id={titleId}/>
                    </Title>
                    {onSwitchView && (
                        <Tooltip title={switchTooltipId ? <FormattedMessage id={switchTooltipId}/> : ''}>
                            <SwitchViewButton onClick={onSwitchView}>
                                <ArrowDouble/>
                            </SwitchViewButton>
                        </Tooltip>
                    )}
                </TitleRow>
                <SubtitleRow>
                    {merchantName && <span>{capitalize(merchantName)}</span>}
                    {subtitleId && <span style={{textTransform: 'none'}}><FormattedMessage id={subtitleId}/></span>}
                    {count && (
                        <Badge
                            rounded
                            indicator="circle"
                            border
                            backgroundColor={greyishBlue50}
                            borderColor={greyishBlue100}
                            labelColor={greyishBlue700}
                            indicatorColor={greyishBlue700}
                        >
                            <span style={{textTransform: 'none'}}>{count}</span>
                        </Badge>
                    )}
                    {/* no scored product means no score at all: a "0" pill would read
                        as a terrible grade rather than as missing data */}
                    {qualityScore > 0 && (
                        <QualityScoreContainer>
                            {merchantName && <span>•</span>}
                            <span><FormattedMessage id={"retailer.product.list.header.qualityScore"}/></span>
                            <ScorePill score={qualityScore}>
                                <ScoreValue score={qualityScore}>{qualityScore}</ScoreValue>
                                <InfoTooltipIcon
                                    titleId={"retailer.product.list.header.scoring.tooltip"}
                                    size={12}
                                    color={scoreColors(qualityScore).text}
                                /></ScorePill>
                        </QualityScoreContainer>
                    )}
                </SubtitleRow>
            </TitlesContainer>
            {onSearchChange && (
                <InputTextField
                    id="search-input"
                    placeholder={searchPlaceholderId && intl.formatMessage({id: searchPlaceholderId})}
                    value={searchValue}
                    onChange={onSearchChange}
                    InputProps={{
                        startAdornment: <SearchIcon size={16}/>,
                    }}
                    sx={searchFieldStyle}
                />
            )}
        </HeaderContainer>
    );
}

export default ListingProductPageHeader;
